import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Search, Edit2, Trash2, Package, AlertTriangle, X } from "lucide-react";
import axios from "axios";
import ImportExportButtons from "../../components/ImportExportButtons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InventoryTab({ user = null }) {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUsageReport, setShowUsageReport] = useState(false);
  const [usageData, setUsageData] = useState([]);
  const [usageLoading, setUsageLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [newItem, setNewItem] = useState({
    name: "", sku: "", quantity: 0, unit_price: 0, min_stock_level: 0, category: ""
  });

  // Check if user is mechanic (hide prices)
  const isMechanic = user?.role === 'mechanic';

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    try {
      const res = await axios.get(`${API}/inventory`);
      setInventory(res.data || []);
    } catch (error) {
      console.error("Error fetching inventory:", error);
      toast.error("Failed to load inventory");
    } finally {
      setLoading(false);
    }
  };

  // Get unique categories
  const categories = [...new Set(inventory.map(i => i.category).filter(Boolean))].sort();
  const lowStockItems = inventory.filter(i => i.quantity < i.min_stock_level);

  const filteredInventory = inventory.filter(item => {
    const matchesSearch = item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.sku?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === "all" || item.category === categoryFilter;
    const matchesLowStock = !showLowStockOnly || item.quantity < item.min_stock_level;
    return matchesSearch && matchesCategory && matchesLowStock;
  });

  // Fetch inventory usage data from repairs
  const fetchUsageReport = async () => {
    setUsageLoading(true);
    try {
      const repairsRes = await axios.get(`${API}/repairs`);
      const repairs = repairsRes.data || [];
      
      // Aggregate usage by item
      const usageMap = {};
      repairs.forEach(repair => {
        (repair.parts_used || []).forEach(part => {
          if (!usageMap[part.item_id]) {
            usageMap[part.item_id] = {
              item_id: part.item_id,
              item_name: part.item_name,
              total_quantity: 0,
              total_cost: 0,
              repairs: []
            };
          }
          usageMap[part.item_id].total_quantity += part.quantity;
          usageMap[part.item_id].total_cost += part.total_cost || (part.quantity * part.unit_price);
          usageMap[part.item_id].repairs.push({
            repair_id: repair.id,
            repair_type: repair.title || repair.repair_type,
            vehicle: repair.vehicle_number,
            date: repair.created_at,
            quantity: part.quantity
          });
        });
      });
      
      setUsageData(Object.values(usageMap).sort((a, b) => b.total_quantity - a.total_quantity));
    } catch (error) {
      console.error("Error fetching usage data:", error);
      toast.error("Failed to load usage report");
    } finally {
      setUsageLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/inventory`, {
        name: newItem.name,
        sku: newItem.sku,
        quantity: parseInt(newItem.quantity),
        unit_price: parseFloat(newItem.unit_price),
        min_stock_level: parseInt(newItem.min_stock_level),
        category: newItem.category
      });
      toast.success("Item created successfully!");
      setShowAddModal(false);
      setNewItem({ name: "", sku: "", quantity: 0, unit_price: 0, min_stock_level: 0, category: "" });
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create item");
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API}/admin/inventory/${editingItem.id}`, {
        name: editingItem.name,
        sku: editingItem.sku,
        quantity: parseInt(editingItem.quantity),
        unit_price: parseFloat(editingItem.unit_price),
        min_stock_level: parseInt(editingItem.min_stock_level),
        category: editingItem.category
      });
      toast.success("Item updated successfully!");
      setEditingItem(null);
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update item");
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    try {
      await axios.delete(`${API}/admin/inventory/${itemId}`);
      toast.success("Item deleted successfully!");
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete item");
    }
  };

  const isLowStock = (item) => item.quantity < item.min_stock_level;

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
              <Package className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />
              Inventory
            </h2>
            <p className="text-white/60 text-sm mt-1">
              {inventory.length} items | {lowStockItems.length} low stock
            </p>
          </div>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            <button
              onClick={() => { setShowUsageReport(true); fetchUsageReport(); }}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded-lg hover:bg-purple-500/30 transition-colors whitespace-nowrap"
              data-testid="usage-report-btn"
            >
              <Package className="w-4 h-4" />
              <span>Usage Report</span>
            </button>
            <ImportExportButtons
              entityName="Inventory"
              exportEndpoint="/admin/inventory/export"
              importEndpoint="/admin/inventory/import"
              templateEndpoint="/admin/inventory/template"
              onImportSuccess={fetchInventory}
              exportFileName="inventory"
            />
            {!isMechanic && (
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors whitespace-nowrap flex-1 sm:flex-none"
                data-testid="add-inventory-btn"
              >
                <Plus className="w-4 h-4" />
                <span>Add Item</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Low Stock Alert */}
      {lowStockItems.length > 0 && (
        <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-400 mb-2">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-semibold">Low Stock Alert</span>
          </div>
          <p className="text-white/80 text-sm">
            {lowStockItems.length} item(s) below minimum stock level: {lowStockItems.map(i => i.name).join(", ")}
          </p>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
          <input
            type="text"
            placeholder="Search items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="inventory-search"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-testid="category-filter"
        >
          <option value="all">All Categories</option>
          {categories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
        <button
          onClick={() => setShowLowStockOnly(!showLowStockOnly)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            showLowStockOnly ? "bg-red-500 text-white" : "bg-white/5 text-white/60 hover:bg-white/10"
          }`}
          data-testid="low-stock-filter"
        >
          <AlertTriangle className="w-4 h-4" />
          Low Stock
        </button>
      </div>

      {/* Inventory Table */}
      <div className="bg-white/5 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="inventory-table">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left p-4 text-white/60 font-medium">Item Name</th>
                <th className="text-left p-4 text-white/60 font-medium">SKU</th>
                <th className="text-left p-4 text-white/60 font-medium">Category</th>
                <th className="text-right p-4 text-white/60 font-medium">Quantity</th>
                <th className="text-right p-4 text-white/60 font-medium">Min Level</th>
                {!isMechanic && <th className="text-right p-4 text-white/60 font-medium">Unit Price</th>}
                {!isMechanic && <th className="text-right p-4 text-white/60 font-medium">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filteredInventory.map((item) => (
                <tr key={item.id} className={`border-b border-white/5 hover:bg-white/5 ${isLowStock(item) ? "bg-red-500/10" : ""}`}>
                  <td className="p-4 text-white">
                    <div className="flex items-center gap-2">
                      {isLowStock(item) && <AlertTriangle className="w-4 h-4 text-red-400" />}
                      {item.name}
                    </div>
                  </td>
                  <td className="p-4 text-white/60 font-mono text-sm">{item.sku || "-"}</td>
                  <td className="p-4 text-white/80">{item.category || "-"}</td>
                  <td className={`p-4 text-right font-semibold ${isLowStock(item) ? "text-red-400" : "text-white"}`}>
                    {item.quantity}
                  </td>
                  <td className="p-4 text-right text-white/60">{item.min_stock_level}</td>
                  {!isMechanic && <td className="p-4 text-right text-green-400">${item.unit_price?.toFixed(2)}</td>}
                  {!isMechanic && (
                    <td className="p-4">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => setEditingItem({ ...item })}
                          className="p-2 text-blue-400 hover:bg-blue-500/20 rounded-lg transition-colors"
                          data-testid={`edit-item-${item.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                          data-testid={`delete-item-${item.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {filteredInventory.length === 0 && (
                <tr>
                  <td colSpan={isMechanic ? 5 : 7} className="p-8 text-center text-white/40">
                    No items found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl p-6 w-full max-w-lg border border-white/10 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-white mb-4">Add Inventory Item</h3>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-white/60 text-sm mb-1">Item Name *</label>
                  <input
                    type="text"
                    value={newItem.name}
                    onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    data-testid="new-item-name"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">SKU / Part Number</label>
                  <input
                    type="text"
                    value={newItem.sku}
                    onChange={(e) => setNewItem({ ...newItem, sku: e.target.value })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="new-item-sku"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">Category</label>
                  <input
                    type="text"
                    value={newItem.category}
                    onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Brakes, Engine"
                    data-testid="new-item-category"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">Quantity *</label>
                  <input
                    type="number"
                    value={newItem.quantity}
                    onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    required
                    data-testid="new-item-quantity"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">Unit Price ($) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newItem.unit_price}
                    onChange={(e) => setNewItem({ ...newItem, unit_price: e.target.value })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    required
                    data-testid="new-item-price"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">Min Stock Level *</label>
                  <input
                    type="number"
                    value={newItem.min_stock_level}
                    onChange={(e) => setNewItem({ ...newItem, min_stock_level: e.target.value })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    required
                    data-testid="new-item-min-level"
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  data-testid="submit-new-item"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl p-6 w-full max-w-lg border border-white/10 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-white mb-4">Edit Inventory Item</h3>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-white/60 text-sm mb-1">Item Name *</label>
                  <input
                    type="text"
                    value={editingItem.name}
                    onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    data-testid="edit-item-name"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">SKU / Part Number</label>
                  <input
                    type="text"
                    value={editingItem.sku || ""}
                    onChange={(e) => setEditingItem({ ...editingItem, sku: e.target.value })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="edit-item-sku"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">Category</label>
                  <input
                    type="text"
                    value={editingItem.category || ""}
                    onChange={(e) => setEditingItem({ ...editingItem, category: e.target.value })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="edit-item-category"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">Quantity *</label>
                  <input
                    type="number"
                    value={editingItem.quantity}
                    onChange={(e) => setEditingItem({ ...editingItem, quantity: e.target.value })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    required
                    data-testid="edit-item-quantity"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">Unit Price ($) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingItem.unit_price}
                    onChange={(e) => setEditingItem({ ...editingItem, unit_price: e.target.value })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    required
                    data-testid="edit-item-price"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-1">Min Stock Level *</label>
                  <input
                    type="number"
                    value={editingItem.min_stock_level}
                    onChange={(e) => setEditingItem({ ...editingItem, min_stock_level: e.target.value })}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    required
                    data-testid="edit-item-min-level"
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setEditingItem(null)}
                  className="flex-1 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  data-testid="submit-edit-item"
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Usage Report Modal */}
      {showUsageReport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-xl p-6 w-full max-w-4xl border border-white/10 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Inventory Usage Report</h3>
              <button
                onClick={() => setShowUsageReport(false)}
                className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {usageLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full"></div>
              </div>
            ) : usageData.length === 0 ? (
              <div className="text-center py-12 text-white/40">
                No inventory usage recorded yet
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-white/60 text-sm">
                  Summary of inventory items used in repairs
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left p-3 text-white/60 font-medium">Item</th>
                        <th className="text-right p-3 text-white/60 font-medium">Total Used</th>
                        {!isMechanic && <th className="text-right p-3 text-white/60 font-medium">Total Cost</th>}
                        <th className="text-right p-3 text-white/60 font-medium">Repairs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usageData.map((usage, idx) => (
                        <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                          <td className="p-3 text-white font-medium">{usage.item_name}</td>
                          <td className="p-3 text-right text-white">{usage.total_quantity}</td>
                          {!isMechanic && <td className="p-3 text-right text-green-400">${usage.total_cost?.toFixed(2)}</td>}
                          <td className="p-3 text-right text-white/60">{usage.repairs.length}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Detailed breakdown */}
                <div className="mt-6">
                  <h4 className="text-lg font-semibold text-white mb-4">Detailed Usage by Repair</h4>
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {usageData.map((usage, idx) => (
                      <div key={idx} className="bg-white/5 rounded-lg p-4">
                        <div className="font-medium text-white mb-2">{usage.item_name}</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                          {usage.repairs.map((repair, rIdx) => (
                            <div key={rIdx} className="flex justify-between text-white/60">
                              <span>{repair.repair_type} - {repair.vehicle || 'N/A'}</span>
                              <span className="text-white">x{repair.quantity}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowUsageReport(false)}
                className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
